import {
  combineReducers,
  type Middleware,
  type Reducer,
} from "@reduxjs/toolkit";

import { ADMIN_POLICY } from "@/apps/admin/api/policy";
import authenticationReducer from "@/apps/admin/features/authentication/authenticationSlice";
import notificationReducer from "@/apps/admin/features/notifications/notificationSlice";
import genFiveApi from "@/state/api";
import type { StoreExtra } from "@/state/api/createBaseQuery";
import { createAppStore } from "@/state/createAppStore";
import classificationReducer from "@/state/slices/classificationSlice";

const BASE_REDUCERS = {
  authentication: authenticationReducer,
  notifications: notificationReducer,
  classification: classificationReducer,
  // The one API both apps share (DEDUPE-2): admin's own routes are injected into it from
  // apps/admin/api/adminOnly.
  [genFiveApi.reducerPath]: genFiveApi.reducer,
};

/**
 * Middleware that arrives with a lazy chunk (GEN5-5269 P6): the store is built
 * at boot with this slot, which passes every action through until a chunk
 * fills it, so that chunk's code is not in `main`.
 */
let lazyMiddleware: Middleware | null = null;
const lazyMiddlewareSlot: Middleware = (api) => (next) => {
  let chain: ((action: unknown) => unknown) | null = null;
  return (action) => {
    if (chain === null && lazyMiddleware !== null) {
      chain = lazyMiddleware(api)(next);
    }
    return chain === null ? next(action) : chain(action);
  };
};

// Built by the shared factory (ARCHITECTURE A5), as before: logger when local, the shared API,
// the lazy slot. A function so tests can build a fresh one (the endpoint characterisation,
// `api/adminEndpoints.requests.test.ts`).
// Focus and reconnect listeners are on (FB-48): they do nothing by themselves, since the API's
// default is no refetch on focus. Only a read that opts in is read again on return (the pipeline's
// live and overview reads, `pages/pipeline/live.ts`), so returning to the tab is no refetch storm.
// Admin's behaviour around a request (verbose toasts, AD1), for every API in this store.
const ADMIN_STORE_EXTRA: StoreExtra = { baseQueryPolicy: ADMIN_POLICY };

export const createAdminStore = () =>
  createAppStore({
    reducers: BASE_REDUCERS,
    middleware: [genFiveApi.middleware, lazyMiddlewareSlot],
    extra: ADMIN_STORE_EXTRA,
    loggerExcludes: [genFiveApi.reducerPath, "__rtkq"],
  });

export const store = createAdminStore();

/**
 * Adds a lazy chunk's slices, and its middleware if it has one, to the running
 * store, once (the shared Genie's, when its dialog loads). Admin's own slices
 * and their state are kept.
 */
let injected = false;
export const injectIntoStore = (
  reducers: Record<string, Reducer>,
  middleware?: Middleware
): void => {
  if (injected) return;
  injected = true;
  lazyMiddleware = middleware ?? null;
  store.replaceReducer(combineReducers({ ...BASE_REDUCERS, ...reducers }));
};

export type AdminDispatch = typeof store.dispatch;
